/**
 * Eager bits of the events hub for the map rail: the market-flag mark (inline SVG) with a
 * time-left chip for the live event, the "new event" dot and the refresh check the map hook
 * uses. Synchronous, no requests: everything comes from the brief the chunk stored.
 */
import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { app, type AppContext } from '../../game/context.js';
import { cacheFetchedAt, liveBriefs, serverNow } from './local.js';
import { shortLeft } from './time.js';

type Ctx = Pick<AppContext, 'services'>;

const INK = '#4a2c2a';
/** the map refreshes the brief at most this often */
const STALE_MS = 60 * 60_000;

/** Market stall with a pennant: the events mark. */
export function EventsMark({ size = 26 }: { size?: number }): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true">
      <path d="M22 2v12" stroke={INK} stroke-width="2" stroke-linecap="round" />
      <path d="M22 3l8 3-8 3z" fill="#8e6cef" stroke={INK} stroke-width="2" stroke-linejoin="round" />
      <rect x="5" y="17" width="22" height="12" rx="2" fill="#fbe3c4" stroke={INK} stroke-width="2" />
      <path d="M3 17l3-6h20l3 6z" fill="#2ec4b6" stroke={INK} stroke-width="2" stroke-linejoin="round" />
      <path d="M10 11l-1.4 6M16 11v6M22 11l1.4 6" stroke="#fff" stroke-width="2" />
      <circle cx="11" cy="23.5" r="3" fill="#f28a2e" stroke={INK} stroke-width="1.6" />
      <circle cx="19" cy="23.5" r="3" fill="#d93a3a" stroke={INK} stroke-width="1.6" />
    </svg>
  );
}

const CHIP: JSX.CSSProperties = {
  position: 'absolute',
  top: '-7px',
  left: '-15px',
  padding: '0 4px',
  borderRadius: '8px',
  border: '2px solid var(--ink)',
  background: 'var(--teal)',
  color: '#fff',
  fontSize: '10px',
  lineHeight: '13px',
  fontWeight: 700,
  whiteSpace: 'nowrap',
};

/** Rail icon: the mark plus the live event's time left ("3d", "5h", "12m"). */
export function EventsIcon({ size = 26 }: { size?: number }): JSX.Element {
  const a = app();
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 60_000);
    return () => clearInterval(id);
  }, []);
  const now = serverNow(a);
  const live = liveBriefs(a, now)[0];
  return (
    <span style={{ position: 'relative', display: 'inline-block', width: `${size}px`, height: `${size}px` }}>
      <EventsMark size={size} />
      {live && (
        <b style={CHIP} data-testid="rail-events-left">
          {shortLeft(live.endsAt - now)}
        </b>
      )}
    </span>
  );
}

/** Dot: an event went live (or changed) since the hub was last opened. */
export function eventsBadge(a: Ctx): true | null {
  return liveBriefs(a, serverNow(a)).some((e) => !e.seen) ? true : null;
}

/** The stored brief is missing or old: the map hook fetches the live list again. */
export function eventsStale(a: Ctx): boolean {
  const at = cacheFetchedAt(a);
  const now = a.services.now();
  return at === null || now - at >= STALE_MS || now < at;
}
